import SafeAppsSDK, { type SafeInfo } from "@safe-global/safe-apps-sdk";
import { SafeAppProvider } from "@safe-global/safe-apps-provider";
import { BrowserProvider, JsonRpcProvider } from "ethers";

import { MAINNET_CHAIN_ID, normalizeOrigin, toAllowedDomainMatchers } from "@/safe/runtimePolicy";

export type SafeDetection = {
  safe?: SafeInfo;
  parentOrigin?: string;
};

export type SafeHandshakeClient = {
  safe: {
    getInfo(): Promise<SafeInfo>;
    getEnvironmentInfo(): Promise<{ origin?: string | null } | undefined>;
  };
};

export const SAFE_HANDSHAKE_TIMEOUT_MS = 2500;

function timeoutAfter(timeoutMs: number): Promise<never> {
  return new Promise((_, reject) =>
    setTimeout(() => reject(new Error("Safe handshake timeout")), timeoutMs)
  );
}

export async function detectSafe(
  sdk: SafeHandshakeClient,
  timeoutMs = SAFE_HANDSHAKE_TIMEOUT_MS
): Promise<SafeDetection> {
  try {
    const [safe, environment] = await Promise.race([
      Promise.all([sdk.safe.getInfo(), sdk.safe.getEnvironmentInfo()]),
      timeoutAfter(timeoutMs)
    ]);

    const parentOrigin = normalizeOrigin(environment?.origin ?? undefined);
    return parentOrigin ? { safe, parentOrigin } : { safe };
  } catch {
    return {};
  }
}

export function createSafeAppsSdk(parentOriginAllowlist: readonly string[]): SafeAppsSDK {
  return new SafeAppsSDK({
    allowedDomains: toAllowedDomainMatchers(parentOriginAllowlist),
    debug: false
  });
}

export function createInSafeProvider(safe: SafeInfo, sdk: SafeAppsSDK): BrowserProvider {
  const safeProvider = new SafeAppProvider(safe, sdk);
  return new BrowserProvider(safeProvider);
}

export function createOutsideSafeProvider(rpcUrl: string): JsonRpcProvider {
  return new JsonRpcProvider(rpcUrl, MAINNET_CHAIN_ID);
}
